import {
  DateFormField,
  PhoneNumberFormField,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import type { MatterIntakeTemplateQuestion } from "@/lib/types";
import type { UseFormRegisterReturn } from "react-hook-form";

interface RenderIntakeFormFieldProps {
  question: MatterIntakeTemplateQuestion;
  register: UseFormRegisterReturn;
  fieldName?: string;
}

export const renderIntakeFormField = ({
  question,
  register,
  fieldName,
}: RenderIntakeFormFieldProps) => {
  const label = question.label;
  const placeholder = `Enter ${label.toLowerCase()}`;

  switch (question.type) {
    case "Multiple Choice":
      return (
        <SelectFormField
          key={fieldName}
          label={label}
          register={register}
          items={(Array.isArray(question.options) ? question.options : [])
            .filter((option): option is string => typeof option === "string")
            .map((option) => ({ value: option, label: option }))}
          itemsKey="value"
          formatItemLabel={(item) => item?.label ?? ""}
          placeholder={`Select ${label.toLowerCase()}`}
        />
      );

    case "Checkbox":
      return (
        <div key={fieldName} className="flex items-center space-x-2">
          <input type="checkbox" {...register} className="h-4 w-4" />
          <label className="text-sm font-medium">{label}</label>
        </div>
      );

    case "Textarea":
      return (
        <TextAreaFormField key={fieldName} label={label} register={register} />
      );

    case "Number":
      return (
        <TextFormField
          key={fieldName}
          label={label}
          type="number"
          register={register}
          placeholder={placeholder}
        />
      );

    case "Date":
      return (
        <DateFormField
          key={fieldName}
          name={register.name}
          label={label}
          register={register}
        />
      );

    case "Email":
      return (
        <TextFormField
          key={fieldName}
          label={label}
          type="email"
          register={register}
          placeholder={placeholder}
        />
      );

    case "Phone Number":
      return (
        <PhoneNumberFormField
          key={fieldName}
          label={label}
          register={register}
        />
      );

    case "File Upload":
      return (
        <div key={fieldName} className="space-y-2">
          <label className="text-sm font-medium">{label}</label>
          <input
            type="file"
            {...register}
            className="file:bg-primary hover:file:bg-primary/90 block w-full text-sm text-gray-500 file:mr-4 file:rounded-full file:border-0 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white"
          />
        </div>
      );

    default:
      return (
        <TextFormField
          key={fieldName}
          label={label}
          register={register}
          placeholder={placeholder}
        />
      );
  }
};
